import React, {
  ReactElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { ALL_NAMESPACE_EVENTS, CoreEvents, TFormRefActions } from '@core';
import { DEFAULT_FORM_ID, EEVents } from '@core/constants';

import { TComponent, TFormProps } from './types';
import ChildWrapper from '@react/Field';
import Submit from '@react/Submit';
import { FormContext } from '@react/context';
import { getFormInstance } from '@core/managers';

const Form = React.forwardRef<TFormRefActions, TFormProps>(
  (
    {
      autoComplete,
      hooks,
      iVars,
      id = DEFAULT_FORM_ID,
      submitOnValidOnly = true,
      initialValues = {},
      className,
      schema,
      group,
      onData,
      onSubmit,
      onBlur,
      onClick,
      afterApiCall,
      onFormMount,
      onFieldMount,
      onStep,
      onFocus,
      onLog,
      onScopeChange,
      onFieldRehydrate,
      renderFieldWrapper,
      disable,
    },
    ref,
  ): ReactElement => {
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const formRef = useRef<HTMLFormElement>(null);
    const submitRef = useRef<HTMLInputElement>(null);
    const { mapper: Mapper, propsMapping: PropsMapping } =
      useContext(FormContext);

    const formInstance = useMemo(() => {
      return getFormInstance(id, {
        group,
        schema,
        newInstance: true,
        initialScope: {
          configs: { ...schema?.configs, enableLogging: !!onLog, disable },
          global: {
            ...schema?.iVars,
            ...iVars,
          },
        },
        initialValues,
      });
    }, []);

    useEffect(() => {
      formInstance.publish(EEVents.ON_FORM_MOUNT);
    }, []);

    const StepWrapper = useMemo(
      () =>
        Mapper.step
          ? (Mapper.step.component as new () => React.Component)
          : React.Fragment,
      [],
    );
    const Children = useMemo(() => {
      if (!formInstance.step.currentStepSchema) {
        return undefined;
      }

      const generateComponentChildren = (
        component: TComponent,
        componentLevel = 0,
      ): any => {
        return component.children?.map((component: TComponent, i: number) => {
          const Component = Mapper[component.component as string]
            ?.component as new () => React.Component;

          const children = (
            <ChildWrapper
              key={`${formInstance.step.index}_${componentLevel}_${component.name}_${component.component}_${i}`}
              component={component}
              wrapper={Component}
              propsMapping={
                PropsMapping[component.component] || PropsMapping.__default__
              }
              formId={id}
              onMount={(data) => {
                onFieldMount &&
                  onFieldMount(formInstance.formData, component, data);
              }}
              onRehydrate={(data) => {
                onFieldRehydrate &&
                  onFieldRehydrate(formInstance.formData, component, data);
              }}
              onChange={(data) => {
                onData && onData(formInstance.formData, component, data);
              }}
              onBlur={(data) => {
                onBlur && onBlur(formInstance.formData, component, data);
              }}
              onClick={(data) => {
                onClick && onClick(formInstance.formData, data);
              }}
              afterApiCall={(data) => {
                afterApiCall &&
                  afterApiCall(formInstance.formData, component, data);
              }}
              onFocus={(data) => {
                onFocus &&
                  onFocus(formInstance.formData, component, {
                    ...data,
                    schemaLocation: {
                      step: formInstance.step.index,
                      depth: componentLevel,
                      index: i,
                    },
                  });
              }}
            >
              {Array.isArray(component.children)
                ? generateComponentChildren(component, componentLevel++)
                : component.children}
            </ChildWrapper>
          );

          if (renderFieldWrapper) {
            return renderFieldWrapper(component, children as any);
          }
          return children;
        });
      };
      return generateComponentChildren(formInstance.step.currentStepSchema);
    }, [formInstance.step.index, id]);

    useEffect(() => {
      formInstance.scope.globalScope = {
        namespace: 'configs',
        data: {
          disable,
        },
      };
    }, [disable]);

    useEffect(() => {
      onFormMount && onFormMount(formInstance.formData);
    }, []);

    useEffect(() => {
      formInstance.scope.globalScope = {
        namespace: 'configs',
        data: {
          disable,
        },
      };
    }, [disable]);

    useEffect(() => {
      formInstance.scope.globalScope = {
        namespace: 'global',
        data: {
          ...schema?.iVars,
          ...iVars,
        },
      };
    }, [JSON.stringify(iVars)]);

    useEffect(
      () =>
        formInstance.subscribe(EEVents.ON_FORM_REHYDRATE, () => {
          forceUpdate();
        }),
      [],
    );
    useEffect(
      () =>
        formInstance.subscribe(EEVents.LOG, ({ data }) => onLog && onLog(data)),
      [],
    );
    useMemo(
      () =>
        formInstance.subscribe(
          ALL_NAMESPACE_EVENTS(EEVents.ON_SCOPE_CHANGE),
          (data) =>
            onScopeChange &&
            onScopeChange(
              formInstance.scope.scope,
              data.data.namespace,
              data.data.key,
            ),
        ),
      [],
    );

    useEffect(
      () =>
        formInstance.subscribe(EEVents.ON_FORM_SUBMIT, ({ data }) => {
          const hookResult =
            hooks?.preSubmit && hooks?.preSubmit(formInstance.formData);
          if (!formInstance.formData.form.isValid && submitOnValidOnly) return;

          onSubmit &&
            onSubmit(data.event, {
              ...formInstance.formData,
              formatted: { ...formInstance.formData.formatted, ...hookResult },
            });
        }),
      [],
    );

    useEffect(
      () =>
        formInstance.subscribeBulk(
          [
            CoreEvents.NAVIGATE_STEP_BACK,
            CoreEvents.NAVIGATE_STEP_FORWARD,
            CoreEvents.NAVIGATE_STEP,
          ],
          () => {
            onStep && onStep(formInstance.formData);
            forceUpdate();
          },
        ),
      [],
    );

    useImperativeHandle(
      ref,
      () => ({
        values: () => formInstance.formData,
        step: formInstance.publishFor(CoreEvents.NAVIGATE_STEP),
        stepForward: formInstance.publishFor(CoreEvents.NAVIGATE_STEP_FORWARD),
        bindFields: formInstance.publishFor(CoreEvents.BIND_FIELDS),
        stepBack: formInstance.publishFor(CoreEvents.NAVIGATE_STEP_BACK),
        validateForm: formInstance.publishFor(CoreEvents.VALIDATE_FORM),
        submit: () => submitRef.current?.click(),
        schema,
      }),
      [],
    );

    return (
      <form
        action={formInstance.scopedSchema.action}
        method={formInstance.scopedSchema.method}
        className={className}
        ref={formRef}
        autoComplete={autoComplete}
        onSubmit={(event) => {
          formInstance.publish(CoreEvents.ON_FORM_SUBMIT, { event });
        }}
      >
        <StepWrapper {...formInstance.step.currentStepSchema?.props}>
          {Children}
        </StepWrapper>
        {!formInstance.scopedSchema.disableFormSubmit && (
          <Submit ref={submitRef} />
        )}
      </form>
    );
  },
);

//If we do not have schema present loading component if defined and render only when we have schema
const SchemaGuard = React.forwardRef<TFormRefActions, TFormProps>(
  (props, ref): ReactElement => {
    if (!props.schema)
      return props.renderLoading ? props.renderLoading() : <></>;

    return <Form {...props} ref={ref} />;
  },
);

export default SchemaGuard;
